<template>
  <page-header-wrapper>
    <div class="erp-list-page baidao-list-page open-api-monitor-page">
      <a-alert
        v-if="config"
        type="info"
        show-icon
        style="margin-bottom: 16px"
        :message="`华住网关：${config.baseUrl}`"
        :description="config.authHint + (config.keyConfigured ? '（当前环境已配置 key）' : '（当前环境未配置 key，外呼会跳过或报错）')" />

      <a-tabs v-model="activeTab">
        <a-tab-pane key="catalog" tab="接口说明">
          <div class="erp-table-card">
            <div class="erp-table-toolbar">
              <div>
                <h3>华住开放接口清单</h3>
                <p>地址、用途、请求与响应字段说明；与后端 HuazhuOpenApiCatalog 保持一致。</p>
              </div>
            </div>
            <a-table
              row-key="path"
              :columns="catalogColumns"
              :data-source="catalog"
              :loading="catalogLoading"
              :pagination="false"
              bordered
              size="middle"
              :scroll="{ x: 1400 }">
              <template slot="active" slot-scope="value">
                <a-tag :color="value ? 'green' : 'default'">{{ value ? '使用中' : '已停用' }}</a-tag>
              </template>
              <template slot="methodPath" slot-scope="value, record">
                <div><a-tag>{{ record.httpMethod }}</a-tag> <code>{{ record.path }}</code></div>
                <small class="muted">{{ record.fullUrlExample }}</small>
              </template>
              <template slot="textBlock" slot-scope="value">
                <pre class="text-block">{{ value }}</pre>
              </template>
            </a-table>
          </div>
        </a-tab-pane>

        <a-tab-pane key="logs" tab="调用记录">
          <p class="muted" style="margin-bottom: 12px">列表仅展示摘要；完整请求/响应请点击「详情」查看。</p>
          <div class="erp-search-card">
            <a-form layout="inline">
              <a-form-item label="接口路径">
                <a-input v-model="logQuery.apiPath" allow-clear placeholder="如 goodsStockSync" style="width: 180px" @pressEnter="loadLogs(true)" />
              </a-form-item>
              <a-form-item label="关键词">
                <a-input v-model="logQuery.keyword" allow-clear placeholder="菜单/操作/请求/响应" style="width: 220px" @pressEnter="loadLogs(true)" />
              </a-form-item>
              <a-form-item label="结果">
                <a-select v-model="logQuery.bizSuccess" allow-clear placeholder="全部" style="width: 120px">
                  <a-select-option :value="1">成功</a-select-option>
                  <a-select-option :value="0">失败</a-select-option>
                </a-select>
              </a-form-item>
              <a-form-item label="时间">
                <a-range-picker v-model="logDateRange" style="width: 240px" />
              </a-form-item>
              <a-form-item>
                <a-button type="primary" icon="search" @click="loadLogs(true)">查询</a-button>
                <a-button icon="redo" @click="resetLogs">重置</a-button>
              </a-form-item>
            </a-form>
          </div>
          <div class="erp-table-card">
            <a-table
              row-key="id"
              :columns="logColumns"
              :data-source="logRows"
              :loading="logLoading"
              :pagination="logPagination"
              bordered
              size="middle"
              :scroll="{ x: 1200 }"
              @change="logTableChanged">
              <template slot="bizSuccess" slot-scope="value">
                <a-tag :color="Number(value) === 1 ? 'green' : 'red'">{{ Number(value) === 1 ? '成功' : '失败' }}</a-tag>
              </template>
              <template slot="actions" slot-scope="value, record">
                <a @click="openLogDetail(record)">详情</a>
              </template>
            </a-table>
          </div>
        </a-tab-pane>
      </a-tabs>

      <a-drawer title="调用详情" :visible="detailVisible" width="720" @close="detailVisible = false">
        <div v-if="detailLoading" style="text-align: center; padding: 48px 0"><a-spin tip="加载详情…" /></div>
        <template v-else-if="detail">
          <p><strong>{{ detail.apiName }}</strong> · {{ detail.httpMethod }} {{ detail.apiPath }}</p>
          <p class="muted">{{ detail.createTime }} · {{ detail.durationMs != null ? detail.durationMs + ' ms' : '—' }}</p>
          <p v-if="detail.sourceMenu || detail.sourceAction">
            <strong>来源：</strong>{{ detail.sourceMenu || '—' }} / {{ detail.sourceAction || '—' }}
          </p>
          <p v-if="detail.caller" class="muted">技术调用方：{{ detail.caller }}</p>
          <h4>请求 URL</h4>
          <pre class="json-block">{{ displayRequestUrl(detail.requestUrl) }}</pre>
          <h4>请求 Body</h4>
          <pre class="json-block">{{ formatJson(detail.requestBody) }}</pre>
          <h4>响应 HTTP {{ detail.responseHttpStatus }}</h4>
          <pre class="json-block">{{ formatJson(detail.responseBody) }}</pre>
          <p v-if="detail.errorMessage" class="error-text">失败摘要：{{ detail.errorMessage }}</p>
        </template>
      </a-drawer>
    </div>
  </page-header-wrapper>
</template>

<script>
import moment from 'moment'
import {
  getOpenApiMonitorConfig,
  listOpenApiCatalog,
  pageOpenApiCallLogs,
  getOpenApiCallLogDetail
} from '@/api/erp/openApiMonitor'

export default {
  name: 'ErpOpenApiMonitor',
  data () {
    return {
      activeTab: 'catalog',
      config: null,
      catalog: [],
      catalogLoading: false,
      catalogColumns: [
        { title: '能力', dataIndex: 'name', width: 160 },
        { title: '方法与地址', scopedSlots: { customRender: 'methodPath' }, width: 280 },
        { title: '用途', dataIndex: 'purpose', width: 220 },
        { title: '请求参数', dataIndex: 'requestParams', scopedSlots: { customRender: 'textBlock' }, width: 260 },
        { title: '返回参数', dataIndex: 'responseParams', scopedSlots: { customRender: 'textBlock' }, width: 260 },
        { title: '调用方', dataIndex: 'caller', width: 200 },
        { title: '失败策略', dataIndex: 'failureStrategy', width: 180 },
        { title: '状态', dataIndex: 'active', width: 90, scopedSlots: { customRender: 'active' } }
      ],
      logQuery: {},
      logDateRange: [],
      logRows: [],
      logLoading: false,
      logPagination: { current: 1, pageSize: 20, total: 0, showSizeChanger: true, showTotal: t => `共 ${t} 条` },
      logColumns: [
        { title: '时间', dataIndex: 'createTime', width: 170 },
        { title: '能力', dataIndex: 'apiName', width: 140, ellipsis: true },
        { title: 'Path', dataIndex: 'apiPath', width: 220, ellipsis: true },
        { title: '结果', dataIndex: 'bizSuccess', width: 80, scopedSlots: { customRender: 'bizSuccess' } },
        { title: 'HTTP', dataIndex: 'responseHttpStatus', width: 70 },
        { title: '来源菜单', dataIndex: 'sourceMenu', width: 120, ellipsis: true },
        { title: '来源操作', dataIndex: 'sourceAction', width: 140, ellipsis: true },
        { title: '失败摘要', dataIndex: 'errorMessage', ellipsis: true },
        { title: '调用方', dataIndex: 'caller', width: 120, ellipsis: true },
        { title: '耗时(ms)', dataIndex: 'durationMs', width: 90 },
        { title: '操作', width: 80, scopedSlots: { customRender: 'actions' } }
      ],
      detailVisible: false,
      detailLoading: false,
      detail: null
    }
  },
  created () {
    this.loadConfig()
    this.loadCatalog()
    this.loadLogs(true)
  },
  methods: {
    loadConfig () {
      getOpenApiMonitorConfig().then(res => { this.config = res.data || null }).catch(e => {
        this.$message.error((e && e.message) || '加载网关配置失败')
      })
    },
    loadCatalog () {
      this.catalogLoading = true
      listOpenApiCatalog().then(res => { this.catalog = res.data || [] }).catch(e => {
        this.$message.error((e && e.message) || '加载接口说明失败，请确认已分配权限 erp:open-api-monitor:list')
      }).finally(() => { this.catalogLoading = false })
    },
    logParams () {
      return {
        ...this.logQuery,
        dateStart: this.logDateRange[0] && this.logDateRange[0].format('YYYY-MM-DD'),
        dateEnd: this.logDateRange[1] && this.logDateRange[1].format('YYYY-MM-DD'),
        pageNum: this.logPagination.current,
        pageSize: this.logPagination.pageSize
      }
    },
    loadLogs (reset) {
      if (reset) this.logPagination.current = 1
      this.logLoading = true
      pageOpenApiCallLogs(this.logParams()).then(res => {
        const page = res.data || {}
        this.logRows = page.records || []
        this.logPagination = {
          ...this.logPagination,
          current: Number(page.current || 1),
          pageSize: Number(page.size || this.logPagination.pageSize),
          total: Number(page.total || 0)
        }
      }).catch(e => {
        this.$message.error((e && e.message) || '加载调用记录失败，请确认已执行 V075/V077 数据库迁移')
      }).finally(() => { this.logLoading = false })
    },
    resetLogs () {
      this.logQuery = {}
      this.logDateRange = []
      this.loadLogs(true)
    },
    logTableChanged (pagination) {
      this.logPagination.current = pagination.current
      this.logPagination.pageSize = pagination.pageSize
      this.loadLogs()
    },
    openLogDetail (record) {
      if (!record || record.id == null) return
      this.detailVisible = true
      this.detailLoading = true
      this.detail = null
      getOpenApiCallLogDetail(record.id).then(res => {
        this.detail = res.data || record
      }).catch(e => {
        this.$message.error((e && e.message) || '加载调用详情失败')
        this.detail = { ...record, requestBody: '', responseBody: '' }
      }).finally(() => {
        this.detailLoading = false
      })
    },
    formatJson (text) {
      if (!text) return '—'
      try {
        return JSON.stringify(JSON.parse(text), null, 2)
      } catch (e) {
        return text
      }
    },
    displayRequestUrl (url) {
      if (!url) return '—'
      if (url.indexOf('key=***') >= 0 && this.config && this.config.openApiKey) {
        return url.replace('key=***', 'key=' + this.config.openApiKey)
      }
      return url
    }
  }
}
</script>

<style scoped>
.text-block {
  white-space: pre-wrap;
  margin: 0;
  font-size: 12px;
  max-height: 120px;
  overflow: auto;
}
.json-block {
  background: #f6f8fa;
  padding: 12px;
  border-radius: 4px;
  max-height: 360px;
  overflow: auto;
  font-size: 12px;
}
.muted { color: rgba(0,0,0,.45); }
.error-text { color: #cf1322; }
</style>
